import { formatSsTermChip, formatSsCode } from './inspectionLabel';
import { daysUntilDue, isOpenPastDue } from './dueDate';
import { JOB_STATUS_LABELS, getJobRowStyle, mailListLabel } from './jobStatusColor';

/**
 * Normalize a job/SS status into a colour bucket for chip text + unified row bg.
 */
export function inspectionStatusColorKey(status, dueDate) {
  if (status === 'OVERDUE' || isOpenPastDue(status, dueDate)) return 'OVERDUE';
  const st = status || 'PENDING';
  if (st === 'Due soon' || st === 'PENDING') return 'PENDING';
  if (['QUERIED', 'AUCTION', 'WAITING'].includes(st)) return 'WAITING';
  if (['ACCEPTED', 'SCHEDULED'].includes(st)) return 'SCHEDULED';
  if (['IN_PROGRESS', 'REPAIRING', 'AFTER_PAPERWORK'].includes(st)) return 'IN_PROGRESS';
  if (st === 'NOTIFIED') return 'NOTIFIED';
  if (st === 'COMPLETED') return 'COMPLETED';
  if (['FAILED', 'REJECTED'].includes(st)) return 'FAILED';
  return st;
}

/** Text class for an SS chip from its status / due. */
export function getSsChipTextClass(status, dueDate) {
  const days = daysUntilDue(dueDate);
  const key = inspectionStatusColorKey(status, dueDate);
  const style = getJobRowStyle(key === 'OVERDUE' ? 'OVERDUE' : status || 'PENDING', days, status === 'Due soon');
  return style.date || 'text-gray-900';
}

function pushSsEntry(map, entry) {
  const code = String(entry.ss_code || '').trim();
  if (!code) return;
  const prev = map.get(code);
  if (!prev) {
    map.set(code, entry);
    return;
  }
  // Prefer real job over building_due; prefer worse (overdue) status
  const prevIsDue = prev._type === 'building_due';
  const nextIsDue = entry._type === 'building_due';
  if (prevIsDue && !nextIsDue) {
    map.set(code, entry);
    return;
  }
  if (!prevIsDue && nextIsDue) return;
  if (entry.overdue && !prev.overdue) map.set(code, entry);
}

function ssEntriesFromRow(row) {
  const out = [];
  if (row._type === 'building_due') {
    const list =
      Array.isArray(row.ss_list) && row.ss_list.length
        ? row.ss_list
        : (row.ss_codes || []).map((c) => ({ ss_code: c }));
    list.forEach((s) => {
      const code = String(s.ss_code || '').trim();
      if (!code) return;
      const due = s.due_date || s.nextDueDate || row.due_date;
      const status = 'Due soon';
      out.push({
        ss_code: code,
        ss_name: s.ss_name || row.ss_name || code,
        chip: formatSsTermChip({
          ...row,
          ss_code: code,
          due_date: due,
          inspection_frequency: s.inspection_frequency || 'ANNUAL',
          ss_location: s.location,
        }),
        status,
        color_key: inspectionStatusColorKey(status, due),
        due_date: due,
        days: daysUntilDue(due),
        overdue: daysUntilDue(due) != null && daysUntilDue(due) < 0,
        default_worker_id: s.default_worker_id || row.default_worker_id || null,
        default_assignee_name:
          s.default_assignee_name || row.default_assignee_name || row.assigned_worker_name || null,
        job_id: null,
        _type: 'building_due',
      });
    });
    return out;
  }

  const code = String(row.ss_code || '').trim();
  if (!code) return out;
  const due = row.due_date;
  const overdue = isOpenPastDue(row.status, row.period_due || due);
  const status = overdue ? 'OVERDUE' : row.status || 'PENDING';
  out.push({
    ss_code: code,
    ss_name: row.ss_name || code,
    chip: formatSsTermChip(row),
    status,
    color_key: inspectionStatusColorKey(status, due),
    due_date: due,
    days: daysUntilDue(due),
    overdue,
    default_worker_id: row.default_worker_id || row.assigned_worker_id || null,
    default_assignee_name: row.default_assignee_name || row.assigned_worker_name || null,
    job_id: row.id || null,
    notify_facts: row.notify_facts,
    schedule_mail_state: row.schedule_mail_state,
    _type: 'job',
  });
  return out;
}

/**
 * Group issued inspection rows by building for the Inspections summary list.
 * One row per building with clickable building name + per-SS chips.
 */
export function groupInspectionsByBuilding(rows) {
  const map = new Map();

  for (const row of rows || []) {
    const bid = row.building_id;
    if (bid == null || bid === '') continue;

    const key = String(bid);
    if (!map.has(key)) {
      const rawName = row.building_name && String(row.building_name).trim();
      map.set(key, {
        building_id: bid,
        building_key: key,
        // Never show "Building #23" — use plain name or "Building"
        building_name: rawName || 'Building',
        items: [],
        ssMap: new Map(),
      });
    }
    const g = map.get(key);
    if (row.building_name && String(row.building_name).trim()) {
      g.building_name = String(row.building_name).trim();
    }
    g.items.push(row);
    ssEntriesFromRow(row).forEach((e) => pushSsEntry(g.ssMap, e));
  }

  return [...map.values()]
    .map((g) => {
      const ss_entries = [...g.ssMap.values()].sort((a, b) =>
        String(a.ss_code).localeCompare(String(b.ss_code), undefined, { numeric: true })
      );

      const dues = ss_entries.map((e) => e.due_date).filter(Boolean);
      let earliest = null;
      for (const d of dues) {
        const t = new Date(d).getTime();
        if (Number.isNaN(t)) continue;
        if (earliest == null || t < earliest) earliest = t;
      }
      const due_date = earliest != null ? new Date(earliest).toISOString().slice(0, 10) : null;

      let overdueCount = 0;
      const statusCounts = {};
      for (const e of ss_entries) {
        if (e.overdue || e.color_key === 'OVERDUE') overdueCount += 1;
        const st = e.status || '—';
        statusCounts[st] = (statusCounts[st] || 0) + 1;
      }

      let statusLabel;
      let primary_status = null;
      if (overdueCount > 0) {
        statusLabel = `Over Due (${overdueCount})`;
        primary_status = 'OVERDUE';
      } else {
        const top = Object.entries(statusCounts).sort((a, b) => b[1] - a[1])[0];
        if (!top) statusLabel = '—';
        else if (top[0] === 'Due soon') {
          statusLabel = `Due soon (${ss_entries.length})`;
          primary_status = 'Due soon';
        } else if (top[0] === 'NOTIFIED') {
          const counts = {};
          ss_entries
            .filter((entry) => entry.status === 'NOTIFIED')
            .forEach((entry) => {
              const label = mailListLabel({ status: 'NOTIFIED', ...entry });
              counts[label] = (counts[label] || 0) + 1;
            });
          statusLabel = Object.entries(counts)
            .map(([label, count]) => `${label} (${count})`)
            .join(', ');
          primary_status = 'NOTIFIED';
        } else {
          statusLabel = `${JOB_STATUS_LABELS[top[0]] || top[0]} (${ss_entries.length})`;
          primary_status = top[0];
        }
      }

      const colorKeys = [...new Set(ss_entries.map((e) => e.color_key).filter(Boolean))];
      const status_unified = colorKeys.length === 1 ? colorKeys[0] : null;

      const iqpByName = new Map();
      ss_entries.forEach((e) => {
        const name = (e.default_assignee_name && String(e.default_assignee_name).trim()) || '';
        if (!name) return;
        if (!iqpByName.has(name)) {
          iqpByName.set(name, {
            name,
            worker_id: e.default_worker_id || null,
            ss_codes: [],
          });
        }
        const rec = iqpByName.get(name);
        if (e.default_worker_id && !rec.worker_id) rec.worker_id = e.default_worker_id;
        if (!rec.ss_codes.includes(e.ss_code)) rec.ss_codes.push(e.ss_code);
      });
      const iqp_people = [...iqpByName.values()];

      const ssPart =
        ss_entries.length === 0
          ? 'No SS'
          : ss_entries
              .slice(0, 12)
              .map((e) => e.chip)
              .join(', ') + (ss_entries.length > 12 ? '…' : '');

      return {
        building_id: g.building_id,
        building_key: g.building_key,
        building_name: g.building_name,
        item_count: g.items.length,
        ss_count: ss_entries.length,
        ss_entries,
        due_date,
        days: daysUntilDue(due_date),
        status_label: statusLabel,
        overdue_count: overdueCount,
        primary_status,
        status_unified,
        inspection_label: `${g.building_name} — ${ssPart}`,
        iqp_people,
        iqp_names: iqp_people.map((p) => p.name),
        iqp_label: iqp_people.map((p) => p.name).join(', '),
        items: g.items,
      };
    })
    .sort((a, b) => {
      if (a.overdue_count !== b.overdue_count) return b.overdue_count - a.overdue_count;
      const da = a.due_date ? new Date(a.due_date).getTime() : Infinity;
      const db = b.due_date ? new Date(b.due_date).getTime() : Infinity;
      if (da !== db) return da - db;
      return String(a.building_name).localeCompare(String(b.building_name));
    });
}

export { formatSsCode };
